import { ElementRef, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { translate } from '@vimeo/i18n-runtime';

import T from '../../../translate';
import { useFeatureFlow } from '../../contexts/FeatureFlowContext';
import { useLead, useNotification, useRefreshConfirmationPage, useRouterPath } from '../../hooks';
import { useGlobalStore } from '../../state/globalStore';
import { Margin, Padding } from '../../styles/commonStyle';
import {
  BUTTON_ROLES,
  BUTTON_TEXT_TYPE,
  FEATURES,
  FORM_FIELD_NAME_VALUES,
  KEY_CODES,
  LOGIN_ERROR_CODES,
  PREVIEW_FORM_FIELDS_WIDTH,
  PREVIEW_TYPE,
} from '../../utils/constants';
import { getIdFromLeadUri, getValidator, setCookie } from '../../utils/helper';
import { AgreeCheckbox } from '../common/AgreeCheckbox';
import { EventDate } from '../common/EventDate';
import { Input } from '../elements';
import { Alignment, Logo, WrapperBox } from '../previewStyles';
import { StylableButton } from '../stylable-button';
import { StylableText } from '../stylable-text';

export const LoginView = () => {
  const leadCaptureProperties = useGlobalStore((state) => state.leadCapture);

  const {
    logo,
    alignment,
    loginPageTitle,
    nonEditableTextStyle: { dontHaveAccount: dontHaveAccountStyle },
  } = leadCaptureProperties;
  const {
    setEmailAddressCached,
    emailAddressCached,
    apiEntityId: entityId,
  } = useGlobalStore((store) => store);
  const emailAddressRef = useRef<HTMLInputElement & ElementRef<typeof Input>>(null);
  const loginTitleRef = useRef<ElementRef<typeof StylableText>>(null);
  const { refresh: refreshConfirmationPageData } = useRefreshConfirmationPage();
  const { onPreviewChange } = useRouterPath();
  const hasConfirmationView = useFeatureFlow(FEATURES.CONFIRMATION_VIEW);

  const agreeCheckboxRef = useRef<ElementRef<typeof AgreeCheckbox>>(null);
  const [agreeCheckboxNotCheckedError, setAgreeCheckboxNotCheckedError] = useState(false);
  const [agreeCheckboxChecked, setAgreeCheckboxChecked] = useState(false);
  const [apiError, setApiError] = useState<string>();
  const [autoFocus, setAutoFocus] = useState(false);

  const { loginEntityLead } = useLead();
  const { show: showNotification } = useNotification();

  const [isLoading, setIsLoading] = useState(false);

  const onCheckboxChecked = (checked: boolean) => {
    setAgreeCheckboxChecked(checked);
    setAgreeCheckboxNotCheckedError(!checked);
  };

  const onEmailBlur = (value: string) => {
    // Save the email address to zustand so available across multiple screen
    setEmailAddressCached(value);
  };

  const loginLead = async () => {
    setIsLoading(true);
    setApiError(undefined);
    const response = await loginEntityLead({ email: emailAddressRef.current?.getValue() || '' });
    if (response?.errorCode) {
      setIsLoading(false);
      switch (response.errorCode) {
        case LOGIN_ERROR_CODES.NOT_REGISTERED:
          setApiError(T.RegisterToSignIn);
          break;
        case LOGIN_ERROR_CODES.INVALID_EMAIL:
          setApiError(T.InvalidEmailAddress);
          break;
        default:
          showNotification({ message: T.UhOhThereWasAProblem });
      }
      return;
    }
    if (response && response['uri']) {
      const responseId = getIdFromLeadUri(response['uri']);
      if (hasConfirmationView) {
        const confirmationPageData = await refreshConfirmationPageData(responseId);
        if (confirmationPageData) {
          setCookie(entityId, responseId);
          onPreviewChange(PREVIEW_TYPE.CONFIRMATION);
          /**
           * In case of multi step form,
           *
           * If login is successful, reset the current field index to point to the first field,
           * so in case if registrant was deleted by admin while confirmation page is kept open,
           * the registrant will see the registration page again starting from the first field.
           * Otherwise registrant will see the last seen field.
           */
          useGlobalStore.setState({ cookie: responseId, currentSelectedFieldIndex: 0 });
        } else {
          showNotification({ message: T.UhOhThereWasAProblem });
        }
      } else {
        setCookie(entityId, responseId);
        useGlobalStore.setState({ cookie: responseId, currentSelectedFieldIndex: 0 });
      }
    } else {
      showNotification({ message: T.UhOhThereWasAProblem });
    }
    setIsLoading(false);
  };

  const submitToApi = async () => {
    const isValidEmail = emailAddressRef.current?.validate();
    setAgreeCheckboxNotCheckedError(!agreeCheckboxChecked);
    agreeCheckboxRef.current?.shakeError();
    if (!isValidEmail) return;
    if (agreeCheckboxChecked == false) {
      return;
    }
    loginLead();
  };

  const enableAutoFocus = () => {
    setAutoFocus(true);
  };

  // In case something is there in the emailCached, put that to inputedData so it displays by default
  useEffect(() => {
    emailAddressRef.current && (emailAddressRef.current.value = emailAddressCached);
    // Need to run this code on initial render only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Alignment align={alignment} addPadding onMouseEnter={enableAutoFocus}>
      {/* + 8 is to accomodate padding */}
      <WrapperBox paddingTop={50} paddingBottom={50} maxWidth={PREVIEW_FORM_FIELDS_WIDTH + 8}>
        {logo.isActive && (
          <a
            {...(logo.isLinkActive && logo.customLink?.length > 0
              ? { href: logo.customLink, target: '_blank' }
              : {})}
          >
            <Logo src={logo.url} />
          </a>
        )}
        <Padding left={4} right={4}>
          <StylableText ref={loginTitleRef} content={loginPageTitle} />
          <EventDate />
          <Input
            autoFocus={autoFocus}
            validator={getValidator(FORM_FIELD_NAME_VALUES.EMAIL)}
            errorMessage={apiError}
            ref={emailAddressRef}
            onBlur={onEmailBlur}
            value={emailAddressCached}
            placeholder={FORM_FIELD_NAME_VALUES.EMAIL}
            onEnterKeyPressed={submitToApi}
          />
          <Margin top={10}>
            <AgreeCheckbox
              ref={agreeCheckboxRef}
              error={agreeCheckboxNotCheckedError}
              checked={agreeCheckboxChecked}
              onChange={(checked) => onCheckboxChecked(checked)}
            />
          </Margin>
          <Margin top={10}>
            <StylableButton
              onClick={submitToApi}
              role={BUTTON_ROLES.PRIMARY}
              textType={BUTTON_TEXT_TYPE.LOGIN}
              loading={isLoading}
            />
          </Margin>

          <Margin top={10} bottom={10}>
            <StyledText color={dontHaveAccountStyle.color}>
              {translate({
                singular: 'Not signed up? {LINK}Register{/LINK}',
                replacements: {
                  LINK: (text: string) => (
                    <StyledLink
                      tabIndex={0}
                      onKeyPress={(e) =>
                        e.key === KEY_CODES.ENTER && onPreviewChange(PREVIEW_TYPE.FORM)
                      }
                      onClick={() => onPreviewChange(PREVIEW_TYPE.FORM)}
                    >
                      {text}
                    </StyledLink>
                  ),
                },
              })}
            </StyledText>
          </Margin>
        </Padding>
      </WrapperBox>
    </Alignment>
  );
};

const StyledLink = styled.u`
  cursor: pointer;
  font-weight: 400;
`;

const StyledText = styled.span<{ color: string }>`
  color: ${({ color }) => color};
  font-size: 12px;
  font-weight: 400;
`;
